import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Search, X } from "lucide-react";
import { Input } from "../../ui/Input";
import { Select } from "../../ui/Select";
import { Button } from "../../ui/Button";
import { getAnnouncementCommuneOptions } from "../../../api/announcements";

// ─────────────────────────────────────────────────────────────────────────────
// Selector de comunas del catálogo (con su `communeId`). Se busca por nombre en
// todo el país o se recorre por región; lo elegido queda como chips.
// ─────────────────────────────────────────────────────────────────────────────

const normalize = (value) =>
  String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();

/**
 * @param {{ countryCode: string, selected: {communeId: string, name: string}[],
 *   onChange: (next: {communeId: string, name: string}[]) => void }} props
 */
export default function CommunePicker({ countryCode, selected, onChange }) {
  const { t } = useTranslation();
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [regionCode, setRegionCode] = useState("");
  const [query, setQuery] = useState("");

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    (async () => {
      const result = await getAnnouncementCommuneOptions(countryCode);
      if (cancelled) return;
      setLoading(false);
      if (!result.ok) {
        setError(t(result.error));
        return;
      }
      setRegions(result.data?.regions ?? []);
    })();
    return () => {
      cancelled = true;
    };
  }, [countryCode, t]);

  const selectedIds = useMemo(() => new Set(selected.map((c) => c.communeId)), [selected]);

  const visible = useMemo(() => {
    const q = normalize(query);
    if (q) {
      return regions
        .flatMap((r) => r.communes)
        .filter((c) => normalize(c.name).includes(q))
        .slice(0, 60);
    }
    return regions.find((r) => r.code === regionCode)?.communes ?? [];
  }, [regions, regionCode, query]);

  const toggle = (commune) => {
    if (selectedIds.has(commune.communeId)) {
      onChange(selected.filter((c) => c.communeId !== commune.communeId));
    } else {
      onChange([...selected, { communeId: commune.communeId, name: commune.name }]);
    }
  };

  const addAllVisible = () => {
    const toAdd = visible
      .filter((c) => !selectedIds.has(c.communeId))
      .map((c) => ({ communeId: c.communeId, name: c.name }));
    onChange([...selected, ...toAdd]);
  };

  if (loading) return <p className="text-sm text-fg-muted">{t("Cargando comunas…")}</p>;
  if (error) return <p className="text-sm text-status-error">{error}</p>;

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <Select
          value={regionCode}
          onChange={(e) => setRegionCode(e.target.value)}
          placeholder={t("Elige una región")}
          options={regions.map((r) => ({ value: r.code, label: r.name }))}
          aria-label={t("Región")}
        />
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-muted" aria-hidden="true" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={t("Buscar comuna")}
            aria-label={t("Buscar comuna")}
            className="pl-9"
          />
        </div>
      </div>

      {visible.length > 0 && (
        <div className="rounded-lg border border-border">
          <div className="flex items-center justify-between border-b border-border px-3 py-2">
            <span className="text-xs text-fg-muted">
              {t("{{n}} comunas", { n: visible.length })}
            </span>
            <Button size="sm" variant="ghost" onClick={addAllVisible}>
              {t("Agregar todas")}
            </Button>
          </div>
          <ul className="max-h-56 overflow-y-auto p-2">
            {visible.map((commune) => (
              <li key={commune.communeId}>
                <label className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-sm text-fg hover:bg-bg-muted">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-brand-500"
                    checked={selectedIds.has(commune.communeId)}
                    onChange={() => toggle(commune)}
                  />
                  {commune.name}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2" aria-label={t("Comunas elegidas")}>
          {selected.map((commune) => (
            <span
              key={commune.communeId}
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-0.5 pl-2.5 pr-1 text-xs font-medium text-brand-700"
            >
              {commune.name}
              <button
                type="button"
                onClick={() => toggle(commune)}
                className="flex h-5 w-5 items-center justify-center rounded-full hover:bg-brand-100"
                aria-label={t("Quitar {{name}}", { name: commune.name })}
              >
                <X className="h-3 w-3" aria-hidden="true" />
              </button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
